import { ExerciseTemplate } from 'exercises/types';
import React, { useState } from 'react';
import styled, { ThemeProps } from 'styled-components';
import { Modal, ModalTitle } from 'styleguide/Modal';
import { View } from 'styleguide/View';
import { H5, P2 } from 'styleguide/Texts';
import { Card } from 'styleguide/Card';
import { ExercisePlaceholderSvg } from 'components/ExercisePlaceholderSvg';
import { CornerRadius, Spacing } from 'styleguide/spacing';
import { colors } from 'styleguide/colors';
import { Theme } from 'styleguide/types';
import { equipmentToHuman, muscleGroupToHuman } from 'exercises/utils';
import { PrimaryButton } from 'styleguide/Buttons';
import { Tabs } from 'screens/Profile/components/Tabs';
import { DividerLine } from 'styleguide/DividerLine';
import { MuscleGroup, rawInstructionsToIndexedSteps } from 'hevy-shared';
import { Media } from 'screens/Exercise/components/types';
import { FlexRow } from 'styleguide/Row';
import { t } from 'translations/t';
import { stores } from 'state/stores';

export interface ExerciseDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  exercise: ExerciseTemplate;
}

type TabOption = 'summary' | 'how-to';

const Container = styled(View)`
  height: 80vh;
  min-width: 600px;
  max-width: 600px;
  @media (max-width: 500px) {
    min-width: 90vw;
  }
`;

const MediaContainer = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 600px;
  aspect-ratio: 1.6 / 1;
  justify-content: center;
  overflow: hidden;
  border-radius: ${CornerRadius.lg}px;
  border: 1px solid
    ${(props: ThemeProps<Theme>) =>
      props.theme.name === 'dark' ? colors.greyUltraDark : colors.greyUltraLight};
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${Spacing.xs}px;
  flex-wrap: wrap;
`;

const ContentColumn = styled(View)`
  width: 100%;
  gap: ${Spacing.md}px;
`;

const InfoColumn = styled(View)`
  gap: ${Spacing.md}px;
  flex: 1;
`;

const TabContentContainer = styled(View)`
  flex: 1;
  padding: ${Spacing.lg}px;
  min-height: 200px;
`;

const BottomContainer = styled(View)`
  align-items: flex-end;
  padding: ${Spacing.md}px;
`;

export const ExerciseDetailModal = ({ isOpen, onClose, exercise }: ExerciseDetailModalProps) => {
  const [selectedTab, setSelectedTab] = useState<TabOption>('summary');

  // Get media similar to ExerciseHeader
  const getMedia = (): Media | undefined => {
    switch (exercise.media_type) {
      case 'video':
        return exercise.url ? { type: 'video', url: exercise.url } : undefined;
      case 'image':
        return exercise.url ? { type: 'image', url: exercise.url } : undefined;
      default:
        if (exercise.thumbnail_url) {
          return { type: 'image', url: exercise.thumbnail_url };
        }
        return undefined;
    }
  };

  const title = stores.exerciseTemplates.getExerciseTitle(exercise.id) || '';
  const media = getMedia();
  const equipment = exercise.equipment_category || 'barbell';
  const muscleGroup = exercise.muscle_group || 'other';
  const otherMuscles: MuscleGroup[] = exercise.other_muscles || [];
  const exerciseInstructions = stores.exerciseTemplates.getExerciseInstructions(exercise.id);

  const tabs = [
    { id: 'summary' as TabOption, title: t('web.exerciseDetailModal.summary') },
    ...(exerciseInstructions
      ? [{ id: 'how-to' as TabOption, title: t('web.exercise.tabs.howTo') }]
      : []),
  ];

  const handleSeeStats = () => {
    window.open(`/exercise/${exercise.id}`, '_blank');
  };

  const instructions = rawInstructionsToIndexedSteps(exerciseInstructions ?? '');

  return (
    <Modal isOpen={isOpen} onClose={onClose} contentStyle={{ padding: 0 }}>
      <Container>
        <View style={{ padding: Spacing.lg, paddingBottom: Spacing.md }}>
          <ModalTitle title={title} onClose={onClose} />
        </View>

        <View>
          <Tabs tabs={tabs} selectedTabIdentifier={selectedTab} onTabClicked={setSelectedTab} />
          <DividerLine />
        </View>

        {selectedTab === 'summary' && (
          <TabContentContainer>
            <ContentColumn>
              {media?.type === 'video' && (
                <MediaContainer>
                  <video
                    key={media.url}
                    loop
                    muted
                    autoPlay
                    playsInline
                    width="100%"
                    style={{ borderRadius: CornerRadius.lg }}
                  >
                    <source src={media.url} type="video/mp4" />
                  </video>
                </MediaContainer>
              )}
              {media?.type === 'image' && (
                <MediaContainer>
                  <Image key={media.url} src={media.url} />
                </MediaContainer>
              )}
              {!media && (
                <MediaContainer style={{ border: '0px' }}>
                  <ExercisePlaceholderSvg diameter={150} />
                </MediaContainer>
              )}
              <InfoColumn>
                <InfoContainer>
                  <P2 style={{ color: colors.grey }}>{t('web.exerciseDetailModal.equipment')}</P2>
                  <P2>{equipmentToHuman(equipment)}</P2>
                </InfoContainer>
                <InfoContainer>
                  <P2 style={{ color: colors.grey }}>{t('web.exerciseDetailModal.primary')} </P2>
                  <P2>{muscleGroupToHuman(muscleGroup)}</P2>
                </InfoContainer>
                {otherMuscles.length > 0 && (
                  <InfoContainer>
                    <P2 style={{ color: colors.grey }}>{t('web.exerciseDetailModal.secondary')}</P2>
                    <P2>{otherMuscles.map(mg => muscleGroupToHuman(mg)).join(', ')}</P2>
                  </InfoContainer>
                )}
              </InfoColumn>
            </ContentColumn>
          </TabContentContainer>
        )}

        {selectedTab === 'how-to' && instructions.length > 0 && (
          <TabContentContainer>
            <View style={{ gap: Spacing.md }}>
              {instructions.map((s, index) => (
                <View key={`exercise-howTo-step-${index}`}>
                  <FlexRow
                    style={{
                      alignItems: 'flex-start',
                      gap: Spacing.md,
                      paddingBottom: Spacing.md,
                    }}
                  >
                    {s.index !== undefined && <H5>{s.index}.</H5>}
                    <P2>{s.description}</P2>
                  </FlexRow>
                  {index < instructions.length - 1 && <DividerLine />}
                </View>
              ))}
            </View>
          </TabContentContainer>
        )}
        <DividerLine />

        <BottomContainer>
          <PrimaryButton
            title={t('web.exerciseDetailModal.seeYourStats')}
            onClick={handleSeeStats}
          />
        </BottomContainer>
      </Container>
    </Modal>
  );
};
